import { useState, useEffect, useCallback } from 'react';
import { adminGet, adminPost, adminPut } from '../lib/api';

interface DemoIdentity {
  name: string;
  role: string;
  ageBracket: string;
  jurisdiction: string;
  jurisdictionLabel: string;
}

interface DemoReset {
  id: string;
  preset: string | null;
  identity: { name: string; role: string; ageBracket: string; jurisdiction: string };
  recordsCleared: number;
  timestamp: string;
}

interface DemoContract {
  id: string;
  filename: string;
  label: string;
  perspective: string;
  description: string;
  flags: string[];
}

interface DemoPreset {
  id: string;
  label: string;
  shortLabel: string;
  identity: DemoIdentity;
  description: string;
}

async function api(path: string, options?: { method?: string; body?: string }) {
  if (options?.method === 'POST') {
    const resp = await adminPost(path, options.body ? JSON.parse(options.body) : undefined);
    return resp.data || {};
  }
  if (options?.method === 'PUT') {
    const resp = await adminPut(path, options.body ? JSON.parse(options.body) : {});
    return resp.data || {};
  }
  const resp = await adminGet(path);
  return resp.data || {};
}

export function DemoModeManager() {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState<DemoIdentity>({
    name: 'Tony Hiscock', role: 'athlete', ageBracket: 'adult',
    jurisdiction: 'CA', jurisdictionLabel: 'California',
  });
  const [recordCount, setRecordCount] = useState(0);
  const [presets, setPresets] = useState<DemoPreset[]>([]);
  const [history, setHistory] = useState<DemoReset[]>([]);
  const [contracts, setContracts] = useState<DemoContract[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [statusRes, presetsRes, historyRes, contractsRes] = await Promise.all([
      api('/api/admin/demo/status'),
      api('/api/admin/demo/presets'),
      api('/api/admin/demo/history?limit=10'),
      api('/api/admin/demo/contracts'),
    ]);
    if (statusRes.active !== undefined) {
      setActive(statusRes.active);
      if (statusRes.identity) setIdentity(statusRes.identity);
      setRecordCount(statusRes.recordCount || 0);
    }
    if (presetsRes.presets) setPresets(presetsRes.presets);
    if (historyRes.resets) setHistory(historyRes.resets);
    if (contractsRes.contracts) setContracts(contractsRes.contracts);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleToggle() {
    setActionLoading('toggle');
    if (active) {
      await api('/api/admin/demo/deactivate', { method: 'POST' });
    } else {
      await api('/api/admin/demo/activate', { method: 'POST' });
    }
    await refresh();
    setActionLoading(null);
  }

  async function handleReset() {
    setActionLoading('reset');
    setShowConfirm(false);
    await api('/api/admin/demo/reset', { method: 'POST' });
    await refresh();
    setActionLoading(null);
  }

  async function handleLoadPreset(presetId: string) {
    setActionLoading(presetId);
    await api(`/api/admin/demo/presets/${presetId}/load`, { method: 'POST' });
    await refresh();
    setActionLoading(null);
  }

  async function handleSaveIdentity() {
    setActionLoading('identity');
    await api('/api/admin/demo/identity', { method: 'PUT', body: JSON.stringify(identity) });
    await refresh();
    setActionLoading(null);
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-dark-500">Loading demo status...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-display font-bold text-white">Demo Mode Manager</h1>
          {active && (
            <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-rights-500/15 text-rights-500 rounded-full animate-pulse">
              Active
            </span>
          )}
        </div>
      </div>

      {/* Toggle Card */}
      <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium">Demo Mode</p>
            <p className="text-sm text-dark-400 mt-1">
              {active ? 'All activity on the Rights site is tagged as demo data and can be reset.' : 'When active, all activity is tagged as demo data.'}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={actionLoading === 'toggle'}
            className={`relative w-14 h-7 rounded-full transition-colors ${active ? 'bg-rights-500' : 'bg-dark-600'}`}
          >
            <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${active ? 'left-8' : 'left-1'}`} />
          </button>
        </div>
        {active && (
          <div className="mt-4 pt-4 border-t border-dark-650/50 flex items-center gap-4">
            <span className="text-sm text-dark-400">Demo records:</span>
            <span className="font-mono text-rights-500 font-semibold">{recordCount}</span>
          </div>
        )}
      </div>

      {active && (
        <>
          {/* Identity + Presets Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Identity Config */}
            <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
              <h3 className="text-white font-medium mb-4">Current Identity</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-dark-400 block mb-1">Name</label>
                  <input type="text" value={identity.name}
                    onChange={e => setIdentity(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-dark-800 text-white text-sm rounded-lg border border-dark-650 focus:border-rights-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-dark-400 block mb-1">Role</label>
                  <div className="flex gap-2">
                    {['athlete', 'sponsor'].map(r => (
                      <button key={r} onClick={() => setIdentity(prev => ({ ...prev, role: r }))}
                        className={`px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${identity.role === r ? 'bg-rights-500/20 text-rights-500 border border-rights-500/30' : 'bg-dark-800 text-dark-400 border border-dark-650'}`}>{r}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-dark-400 block mb-1">Age Bracket</label>
                  <div className="flex gap-2">
                    {(['adult', 'minor', 'hs_minor'] as const).map(v => {
                      const labels: Record<string, string> = { adult: 'Adult 18+', minor: 'Minor 13-17', hs_minor: 'HS Minor' };
                      const l = labels[v];
                      return (
                      <button key={v} onClick={() => setIdentity(prev => ({ ...prev, ageBracket: v }))}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${identity.ageBracket === v ? 'bg-rights-500/20 text-rights-500 border border-rights-500/30' : 'bg-dark-800 text-dark-400 border border-dark-650'}`}>{l}</button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-dark-400 block mb-1">Jurisdiction</label>
                  <select value={identity.jurisdiction}
                    onChange={e => setIdentity(prev => ({ ...prev, jurisdiction: e.target.value, jurisdictionLabel: e.target.options[e.target.selectedIndex].text }))}
                    className="w-full px-3 py-2 bg-dark-800 text-white text-sm rounded-lg border border-dark-650 focus:border-rights-500 focus:outline-none">
                    {[['CA','California'],['TX','Texas'],['FL','Florida'],['AZ','Arizona'],['AR','Arkansas'],['AL','Alabama'],['NY','New York'],['GA','Georgia'],['TN','Tennessee'],['NM','New Mexico']].map(([v,l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <button onClick={handleSaveIdentity} disabled={actionLoading === 'identity'}
                  className="w-full mt-2 px-4 py-2 text-sm bg-dark-800 text-rights-500 rounded-lg border border-rights-500/20 hover:bg-rights-500/10 transition-colors">
                  {actionLoading === 'identity' ? 'Saving...' : 'Save Identity'}
                </button>
              </div>

              {/* Reset Button */}
              <button onClick={() => setShowConfirm(true)}
                className="w-full mt-4 px-4 py-2.5 text-sm bg-severity-high/10 text-severity-high rounded-lg border border-severity-high/20 hover:bg-severity-high/20 transition-colors font-medium">
                Reset Demo Session ({recordCount} records)
              </button>
            </div>

            {/* Quick Presets */}
            <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
              <h3 className="text-white font-medium mb-4">Quick Presets</h3>
              <div className="space-y-3">
                {presets.map(p => (
                  <button key={p.id} onClick={() => handleLoadPreset(p.id)}
                    disabled={actionLoading === p.id}
                    className="w-full flex items-center gap-4 p-4 bg-dark-800 rounded-xl border border-dark-650 hover:border-rights-500/30 hover:bg-dark-750 transition-colors text-left">
                    <div className="w-10 h-10 rounded-lg bg-rights-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-rights-500 text-lg">
                        {p.identity.ageBracket === 'hs_minor' ? 'shield_person' : p.identity.ageBracket === 'minor' ? 'family_restroom' : 'person'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium">{p.label}</div>
                      <div className="text-xs text-dark-400 mt-0.5 truncate">{p.description}</div>
                    </div>
                    <span className="text-xs text-dark-500 flex-shrink-0">
                      {actionLoading === p.id ? 'Loading...' : 'Load + Reset'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* History + Contracts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Reset History */}
            <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
              <h3 className="text-white font-medium mb-4">Reset History</h3>
              {history.length === 0 ? (
                <p className="text-sm text-dark-500 text-center py-4">No resets yet</p>
              ) : (
                <div className="space-y-2">
                  {history.map(h => (
                    <div key={h.id} className="flex items-center justify-between py-2 border-b border-dark-650/30 last:border-0">
                      <div>
                        <span className="text-sm text-white">
                          {new Date(h.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                        <span className="text-sm text-dark-400 ml-2">
                          {h.preset ? `Preset: ${h.preset}` : 'Manual reset'}
                        </span>
                      </div>
                      <span className="text-xs text-dark-500">{h.recordsCleared} records</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Demo Contracts */}
            <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
              <h3 className="text-white font-medium mb-4">Demo Contracts</h3>
              <div className="space-y-3">
                {contracts.map(c => (
                  <div key={c.id} className="p-4 bg-dark-800 rounded-xl border border-dark-650">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-dark-400 mt-0.5">description</span>
                      <div className="flex-1">
                        <div className="text-sm text-white font-medium">{c.label}</div>
                        <div className="text-xs text-dark-400 mt-1">{c.description}</div>
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {c.flags.map(f => (
                            <span key={f} className="px-2 py-0.5 text-[10px] bg-severity-medium/10 text-severity-medium rounded-full">
                              {f.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => window.open(`https://rights.tattlehash.com/upload.html?demo_contract=${c.id}`, '_blank')}
                      className="w-full mt-3 px-3 py-1.5 text-xs bg-dark-700 text-rights-500 rounded-lg border border-rights-500/20 hover:bg-rights-500/10 transition-colors">
                      Load into Pipeline
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reset Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowConfirm(false)}>
          <div className="bg-dark-700 rounded-2xl border border-dark-650 p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-display font-semibold text-white mb-2">Reset Demo Session?</h3>
            <p className="text-sm text-dark-400 mb-4">
              This will permanently delete <strong className="text-white">{recordCount}</strong> demo records
              for <strong className="text-white">{identity.name}</strong>. Production data is never affected.
            </p>
            <p className="text-xs text-severity-medium mb-6">This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm bg-dark-800 text-dark-300 rounded-lg hover:bg-dark-650 transition-colors">Cancel</button>
              <button onClick={handleReset}
                className="px-4 py-2 text-sm bg-severity-high text-white rounded-lg hover:bg-severity-critical transition-colors">
                {actionLoading === 'reset' ? 'Resetting...' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
